import * as Dfns from 'date-fns/fp';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { datasetRows, fetchHroasResults, HroasResultsBundle } from './hroasResults';
import { parseChannelColumn } from './hroasUtils';
import { isHiddenChannel } from './tinuitiLiveFilters';

const HROAS_QUERY_KEY_PREFIX = 'hroas';

/**
 * Shared fetch for the EF half of the bundle (weekly attribution + budget
 * linear). Keyed on the derived kpi + the selected run so EF reads
 * `runs/{runId}/` instead of `latest/`; a null runId is only ever reached
 * behind an `enabled: !!runId` gate.
 */
const fetchEfBundle = (
  company: string,
  kpi: string,
  runId: string | null
): Promise<HroasResultsBundle> => fetchHroasResults(company, kpi, 'ef', runId);

const weeklyAttributionRows = (bundle: HroasResultsBundle): Record<string, unknown>[] =>
  datasetRows(bundle.data.combinedHolisticWeeklyAttribution);

const budgetLinearRows = (bundle: HroasResultsBundle): Record<string, unknown>[] =>
  datasetRows(bundle.data.combinedBudgetLinear);

export const getMarketingOutcomesDateRangeQueryKey = (
  company: string,
  kpi: string,
  runId: string | null
): readonly unknown[] => [
  HROAS_QUERY_KEY_PREFIX,
  'efficientFrontier',
  'marketingOutcomesDateRange',
  company,
  kpi,
  runId ?? null
];

export interface MarketingOutcomesDateRange {
  maxEnd: string;
  minStart: string;
}

const computeMarketingOutcomesDateRange = (
  rows: Record<string, unknown>[]
): MarketingOutcomesDateRange => {
  const dates = rows
    .map(row => row.date as string | undefined)
    .filter((d): d is string => typeof d === 'string' && d.length > 0);

  if (dates.length === 0) {
    return {
      minStart: Dfns.format('yyyy-MM-dd')(Dfns.subMonths(1, new Date())),
      maxEnd: TODAY
    };
  }

  const sorted = [...dates].sort();
  return { minStart: sorted[0], maxEnd: sorted[sorted.length - 1] };
};

export const fetchMarketingOutcomesDateRange = async (
  company: string,
  kpi: string,
  runId: string | null
): Promise<MarketingOutcomesDateRange> =>
  computeMarketingOutcomesDateRange(
    weeklyAttributionRows(await fetchEfBundle(company, kpi, runId))
  );

export const useMarketingOutcomesDateRange = (
  company: string,
  kpi: string,
  runId: string | null
): UseQueryResult<MarketingOutcomesDateRange, unknown> =>
  useQuery({
    queryKey: getMarketingOutcomesDateRangeQueryKey(company, kpi, runId),
    queryFn: () => fetchMarketingOutcomesDateRange(company, kpi, runId),
    enabled: Boolean(company) && Boolean(runId)
  });

export interface MarketingOutcomesProcessedRow {
  totalBudget: number;
  modelType: string;
  modelScope: string;
  isBau: boolean;
  modelRevenue: number;
  modelRoas: number;
}

export interface MarketingOutcomesData {
  processedData: MarketingOutcomesProcessedRow[];
  budgetKeyMapping: Map<number, number>;
}

export interface ChannelMixBudgetData {
  totalBudget: number;
  channels: { channel: string; featureSpend: number }[];
}

/** Query key for prefetching marketing outcomes */
export const getMarketingOutcomesQueryKey = (
  company: string,
  kpi: string,
  runId: string | null,
  start: string,
  end: string
): readonly unknown[] => [
  HROAS_QUERY_KEY_PREFIX,
  'efficientFrontier',
  'marketingOutcomes',
  company,
  kpi,
  runId ?? null,
  start,
  end
];

/** Query key for prefetching channel mix */
export const getChannelMixQueryKey = (
  company: string,
  kpi: string,
  runId: string | null,
  planLength: number
): readonly unknown[] => [
  HROAS_QUERY_KEY_PREFIX,
  'efficientFrontier',
  'channelMix',
  company,
  kpi,
  runId ?? null,
  planLength
];

export const processMarketingOutcomes = (
  rows: Record<string, unknown>[],
  start: string,
  end: string
): MarketingOutcomesData => {
  const rawParsedData = rows.map(row => ({
    date: row.date as string | undefined,
    totalBudget: Number(row.actual_budget),
    modelType: String(row.model_type ?? ''),
    modelScope: String(row.model_scope ?? ''),
    modelRevenue: Number(row.model_revenue),
    modelRoas: Number(row.model_roas),
    bau: Boolean(row.bau)
  }));

  const filteredByDate = rawParsedData.filter(row => {
    if (!row.date) {
      return false;
    }
    return row.date >= start && row.date <= end;
  });

  const aggregationMap = new Map<
    string,
    {
      weeklyBudget: number;
      modelType: string;
      modelScope: string;
      isBau: boolean;
      totalBudget: number;
      totalRevenue: number;
      countedDates: Set<string>;
    }
  >();

  filteredByDate.forEach(row => {
    const key = `${row.totalBudget}-${row.modelType}-${row.modelScope}`;
    if (!aggregationMap.has(key)) {
      aggregationMap.set(key, {
        weeklyBudget: row.totalBudget,
        modelType: row.modelType,
        modelScope: row.modelScope,
        isBau: row.bau,
        totalBudget: 0,
        totalRevenue: 0,
        countedDates: new Set()
      });
    }
    const entry = aggregationMap.get(key)!;
    // Slots share one g(x), so a date's budget repeats on every slot row but counts once.
    const dateKey = row.date ?? '';
    if (!entry.countedDates.has(dateKey)) {
      entry.countedDates.add(dateKey);
      entry.totalBudget += row.totalBudget;
    }
    entry.totalRevenue += row.modelRevenue;
  });

  const budgetKeyMapping = new Map<number, number>();
  aggregationMap.forEach(entry => {
    if (!budgetKeyMapping.has(entry.weeklyBudget)) {
      budgetKeyMapping.set(entry.weeklyBudget, entry.totalBudget);
    }
  });

  const processedData: MarketingOutcomesProcessedRow[] = Array.from(aggregationMap.values()).map(
    entry => ({
      totalBudget: entry.totalBudget,
      modelType: entry.modelType,
      modelScope: entry.modelScope,
      isBau: entry.isBau,
      modelRevenue: entry.totalRevenue,
      modelRoas: entry.totalRevenue / entry.totalBudget
    })
  );

  return { processedData, budgetKeyMapping };
};

/** Fetch marketing outcomes - used by hook and prefetch */
export const fetchMarketingOutcomesData = async (
  company: string,
  kpi: string,
  runId: string | null,
  start: string,
  end: string
): Promise<MarketingOutcomesData> =>
  processMarketingOutcomes(
    weeklyAttributionRows(await fetchEfBundle(company, kpi, runId)),
    start,
    end
  );

const processChannelMix = (
  rows: Record<string, unknown>[],
  planLength: number
): ChannelMixBudgetData[] => {
  const budgetMap = new Map<
    number,
    { totalBudget: number; channels: { channel: string; featureSpend: number }[] }
  >();

  rows.forEach(row => {
    const r = row;
    // New-format rows leave `channel` blank and carry the raw id in `feature_id` instead (matches
    // the SES/RC/MDD convention); fall back to it so downstream resolution — the
    // isRawFeatureId/resolveChannelName pass in EfficientFrontier.tsx — has something to resolve.
    // Old-format rows already have `channel` populated, so this is a no-op for poppi/tinuititest.
    const rawChannel = String(r.channel ?? '');
    const channel = rawChannel.trim() ? rawChannel : String(r.feature_id ?? '');
    // Tinuiti Live: drop hidden channels from the channel-mix breakdown. The
    // CSV uses comma-separated names like "Podcast, Direct" alongside plain
    // "Super Bowl"; mirror the dual-format detection used elsewhere so both
    // variants are caught. totalBudget is left at its original (model-
    // computed) value, so visible bars sum to less than totalBudget — by
    // design, since totalBudget aligns with the Marketing Outcomes y-axis.
    const isNewFormat = !channel.includes('__');
    const { channel: parsedChannel } = parseChannelColumn(
      isNewFormat ? channel : undefined,
      channel
    );
    if (isHiddenChannel(parsedChannel)) {
      return;
    }
    const budget = r.actual_budget as number;
    if (!budgetMap.has(budget)) {
      budgetMap.set(budget, { totalBudget: budget, channels: [] });
    }
    const budgetData = budgetMap.get(budget)!;
    budgetData.channels.push({
      channel,
      featureSpend: Number(r.feature_spend ?? 0) * planLength
    });
  });

  return Array.from(budgetMap.values());
};

/** Fetch channel mix - used by hook and prefetch */
export const fetchChannelMixData = async (
  company: string,
  kpi: string,
  runId: string | null,
  planLength: number
): Promise<ChannelMixBudgetData[]> =>
  processChannelMix(budgetLinearRows(await fetchEfBundle(company, kpi, runId)), planLength);

export const useMarketingOutcomesData = (
  company: string,
  kpi: string,
  runId: string | null,
  start: string,
  end: string
): UseQueryResult<MarketingOutcomesData, unknown> =>
  useQuery({
    queryKey: getMarketingOutcomesQueryKey(company, kpi, runId, start, end),
    queryFn: () => fetchMarketingOutcomesData(company, kpi, runId, start, end),
    enabled: Boolean(company) && Boolean(runId),
    // EfficientFrontier.tsx already surfaces this query's error via useSetError — suppress the
    // global QueryCache fallback (src/main.tsx) so it doesn't double-show the dialog.
    meta: { suppressGlobalError: true }
  });

export const useChannelMixData = (
  company: string,
  kpi: string,
  runId: string | null,
  planLength: number
): UseQueryResult<ChannelMixBudgetData[], unknown> =>
  useQuery({
    queryKey: getChannelMixQueryKey(company, kpi, runId, planLength),
    queryFn: () => fetchChannelMixData(company, kpi, runId, planLength),
    enabled: Boolean(company) && Boolean(runId),
    // EfficientFrontier.tsx already surfaces this query's error via useSetError — suppress the
    // global QueryCache fallback (src/main.tsx) so it doesn't double-show the dialog.
    meta: { suppressGlobalError: true }
  });
